/**
 * Web ↔ Worker sync layer.
 *
 * Best-effort posting: every write is idempotent on the server (uid+date
 * primary key), and outbound calls are queued in IDB if offline. The queue
 * drains on `online` events and on next solve.
 *
 * Reads (daily metadata, leaderboard) are best-effort GETs with a short
 * timeout — UI never blocks waiting for the worker.
 */
import { idbGet, idbSet } from './idb';
import { profile } from './profile';
import type { SolveRecord } from './profile';

const STORAGE_KEY = 'sync.queue.v1';
const TIMEOUT_MS = 4000;
const RETRY_MAX = 3;

interface QueuedScore {
  kind: 'score';
  payload: ScorePayload;
  attempts: number;
}
interface QueuedUser {
  kind: 'user';
  payload: { uid: string; country: string; locale: string };
  attempts: number;
}
type Queued = QueuedScore | QueuedUser;

interface ScorePayload {
  uid: string;
  date: string;
  time_ms: number;
  attempts: number;
  rarity: string;
  mode: string;
  country: string;
  edges: number;
  idem_key: string;
}

// FDT-O-010b fix — the default was https://api.fudeto.app, which is NOT a
// configured domain (the project lives on smartrich.ai); shipped builds could
// never reach the backend, so rank / percentile / leaderboard silently failed.
// Point at the real deployed worker. VITE_API_BASE still overrides if set.
let baseUrl: string =
  (import.meta as ImportMeta & { env: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  'https://fudeto-api.yves221.workers.dev';
let draining = false;

export function setApiBase(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

/**
 * Absolute API URL for a path. Callers MUST use this (not a bare relative
 * `/v1/...`) — on the native shell the origin is capacitor://localhost, so a
 * relative fetch never reaches the deployed worker and silently fails.
 */
export function apiUrl(path: string): string {
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

async function loadQueue(): Promise<Queued[]> {
  return (await idbGet<Queued[]>(STORAGE_KEY)) ?? [];
}
async function saveQueue(q: Queued[]): Promise<void> {
  await idbSet(STORAGE_KEY, q);
}

async function fetchJson<T>(
  url: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const { timeoutMs = TIMEOUT_MS, ...rest } = init;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...rest, signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** Register or refresh anonymous user. Idempotent. */
export async function registerUser(): Promise<void> {
  const p = profile().get();
  const payload = {
    uid: p.uid,
    country: p.country,
    locale: p.localeOverride ?? navigator.language.slice(0, 12),
  };
  try {
    await fetchJson(`${baseUrl}/v1/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    const q = await loadQueue();
    q.push({ kind: 'user', payload, attempts: 0 });
    await saveQueue(q);
  }
}

/** Post a solve; returns the server-assigned rank + new absolute rating if
 *  reachable, else nulls. (FDT-O-010 — rating drives the player's tier.) */
export async function postSolve(
  record: SolveRecord,
  edges: number,
): Promise<{ rank: number | null; rating: number | null }> {
  const p = profile().get();
  const idemKey = `${p.uid}-${record.date}`;
  const payload: ScorePayload = {
    uid: p.uid,
    date: record.date,
    time_ms: record.timeMs,
    attempts: record.attempts,
    rarity: record.rarity,
    mode: record.mode,
    country: p.country,
    edges,
    idem_key: idemKey,
  };
  try {
    const res = await fetchJson<{ ok: boolean; rank: number | null; rating?: number }>(
      `${baseUrl}/v1/score`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Idempotency-Key': idemKey },
        body: JSON.stringify(payload),
      },
    );
    return { rank: res.rank ?? null, rating: res.rating ?? null };
  } catch {
    const q = await loadQueue();
    q.push({ kind: 'score', payload, attempts: 0 });
    await saveQueue(q);
    return { rank: null, rating: null };
  }
}

/**
 * FDT-O-010b — record a Practice stage solve time and get the REAL speed
 * percentile back. percentile is null until the stage has enough samples
 * (no fake data). Best-effort: returns null on offline / error.
 */
export async function postStageScore(
  chapter: number,
  stage: number,
  timeMs: number,
  edges: number,
): Promise<{ percentile: number | null; sample_n: number }> {
  const uid = profile().get().uid;
  try {
    return await fetchJson<{ percentile: number | null; sample_n: number }>(
      `${baseUrl}/v1/stage-score`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, chapter, stage, time_ms: timeMs, edges }),
        timeoutMs: 2500,
      },
    );
  } catch {
    return { percentile: null, sample_n: 0 };
  }
}

/** Drain queued offline writes. Called on `online` event and at boot. */
export async function drainQueue(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    let q = await loadQueue();
    if (q.length === 0) return;
    const next: Queued[] = [];
    for (const item of q) {
      if (item.attempts >= RETRY_MAX) continue; // give up
      try {
        if (item.kind === 'score') {
          await fetchJson(`${baseUrl}/v1/score`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Idempotency-Key': item.payload.idem_key,
            },
            body: JSON.stringify(item.payload),
          });
        } else {
          await fetchJson(`${baseUrl}/v1/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(item.payload),
          });
        }
      } catch {
        next.push({ ...item, attempts: item.attempts + 1 });
      }
    }
    q = next;
    await saveQueue(q);
  } finally {
    draining = false;
  }
}

/** Read today's solver count + top time. Cached short by client. */
export async function fetchDaily(
  date: string,
): Promise<{ solver_count: number; top_time_ms: number | null } | null> {
  try {
    return await fetchJson<{ solver_count: number; top_time_ms: number | null }>(
      `${baseUrl}/v1/daily/${date}`,
      { timeoutMs: 2500 },
    );
  } catch {
    return null;
  }
}

export interface LeaderboardRow {
  rank: number;
  uid: string;
  time_ms: number;
  country: string;
}
export async function fetchLeaderboard(date: string): Promise<LeaderboardRow[]> {
  try {
    const r = await fetchJson<{ top: LeaderboardRow[] }>(`${baseUrl}/v1/leaderboard/${date}`, {
      timeoutMs: 3000,
    });
    return r.top ?? [];
  } catch {
    return [];
  }
}

/** Country aggregate. */
export interface CountryRow {
  rank: number;
  country: string;
  solvers: number;
  top_time_ms: number;
}
export async function fetchCountryLeaderboard(date: string): Promise<CountryRow[]> {
  try {
    const r = await fetchJson<{ top: CountryRow[] }>(`${baseUrl}/v1/leaderboard/country/${date}`, {
      timeoutMs: 3000,
    });
    return r.top ?? [];
  } catch {
    return [];
  }
}

/** PR (Percentile Rank) — hero competitive metric over the last 30 days. */
export interface PRResult {
  locked: boolean;
  pr_country: number | null;
  pr_global: number | null;
  sample_n: number;
  total_solves: number;
  window_days: number;
}
/**
 * Fetch the player's PR. Returns null on failure (offline / endpoint not yet
 * deployed) so the caller falls back to the locked/unlock state — never a fake %.
 */
export async function fetchPR(uid: string): Promise<PRResult | null> {
  try {
    return await fetchJson<PRResult>(`${baseUrl}/v1/pr/${uid}`, { timeoutMs: 3000 });
  } catch {
    return null;
  }
}

/** Country weekly season row. */
export interface WeeklyCountryRow {
  rank: number;
  code: string;
  score: number;
  players: number;
  /** Real solver count for THIS country TODAY (UTC) — drives the 本日のソルバー column. */
  todaySolvers: number;
}

/** Fetch weekly country season leaderboard. Returns [] on failure. */
export async function fetchWeeklyCountry(): Promise<WeeklyCountryRow[]> {
  try {
    const r = await fetchJson<{
      top: Array<{
        country: string;
        score: number;
        players: number;
        rank: number;
        today_solvers?: number;
      }>;
    }>(`${baseUrl}/v1/leaderboard/country/weekly`, { timeoutMs: 3000 });
    if (!Array.isArray(r.top)) return [];
    return r.top.map((row, i) => ({
      rank: row.rank ?? i + 1,
      code: row.country,
      score: row.score,
      players: row.players,
      todaySolvers: row.today_solvers ?? 0,
    }));
  } catch {
    return [];
  }
}

/** User's weekly rank row. percentile hidden when totalSolves < 10 (v2.1 Bayesian Elo). */
export interface UserWeekRank {
  uid: string;
  week_start: number;
  score: number;
  rank: number;
  percentile: number | null;
}

/** Fetch current user's weekly rank. Returns null on failure. */
export async function fetchUserWeekRank(uid: string, totalSolves: number): Promise<UserWeekRank | null> {
  if (!uid) return null;
  try {
    const r = await fetchJson<UserWeekRank>(
      `${baseUrl}/v1/leaderboard/user?uid=${encodeURIComponent(uid)}&total_solved=${totalSolves}`,
      { timeoutMs: 3000 },
    );
    if (totalSolves < 10) r.percentile = null;
    return r;
  } catch {
    return null;
  }
}

/**
 * Cloud profile snapshot — cross-device restore (RevenueCat-anchored key).
 * pushSnapshot persists; pullSnapshot fetches. Both best-effort (return
 * false/null on failure) so they never block or break the offline-first flow.
 */
export async function pushSnapshot(key: string, snapshot: unknown): Promise<boolean> {
  try {
    const r = await fetchJson<{ ok?: boolean; persisted?: boolean }>(
      apiUrl('/v1/account/snapshot'),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, snapshot }),
        timeoutMs: 5000,
      },
    );
    return r.persisted === true;
  } catch {
    return false;
  }
}

export async function pullSnapshot<T = unknown>(key: string): Promise<T | null> {
  try {
    const r = await fetchJson<{ snapshot: T | null }>(
      apiUrl(`/v1/account/snapshot/${encodeURIComponent(key)}`),
      { timeoutMs: 5000 },
    );
    return r.snapshot ?? null;
  } catch {
    return null;
  }
}

/**
 * P-2.1.4 — Active-now count for the G1 Live Pulse. Returns 0 on failure.
 * Routed through baseUrl (session-811 P0-3): a relative fetch('/v1/…')
 * resolves to capacitor://localhost inside the native shells and always
 * fails — the same defect class FDT-O-010b fixed for the sync queue.
 */
export async function getActiveNow(): Promise<number> {
  try {
    const json = await fetchJson<{ count?: number }>(`${baseUrl}/v1/live/active-now`, {
      timeoutMs: 2000,
    });
    return typeof json.count === 'number' ? json.count : 0;
  } catch {
    return 0;
  }
}

/** P-2.2.3 — Server UTC time for the G4 countdown. Falls back to local clock. */
export async function getServerTime(): Promise<number> {
  try {
    const json = await fetchJson<{ utcMs?: number }>(`${baseUrl}/v1/time`, {
      timeoutMs: 2000,
    });
    return typeof json.utcMs === 'number' ? json.utcMs : Date.now();
  } catch {
    return Date.now();
  }
}

/** Wire boot — register + start auto-drain. */
export function initSync(): void {
  void registerUser();
  void drainQueue();
  window.addEventListener('online', () => void drainQueue());
}
